
import { useRef, useEffect, useCallback } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { fetchUnifiedResults } from '@/services/searchService';
import { groupResultsBySource } from '@/components/search/utils/searchResultsUtils';
import { SEARCH_CONSTANTS } from '@/utils/searchConstants';

interface UseSearchOperationsProps {
  query: string;
  setResults: (results: { items: any[]; total: number }) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setIsOpen: (open: boolean) => void;
  resetResults: () => void;
  setHasSearchCompleted: (completed: boolean) => void;
}

export function useSearchOperations({
  query,
  setResults,
  setIsLoading,
  setError,
  setIsOpen,
  resetResults,
  setHasSearchCompleted
}: UseSearchOperationsProps) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_CONSTANTS.DEBOUNCE_TIME);

  const abortCurrentSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Core search execution function - used by both auto and direct search
  const executeSearch = useCallback(async (searchQuery: string) => {
    // Abort any existing search
    abortCurrentSearch();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setIsLoading(true);
      setError(null);

      const searchResults = await fetchUnifiedResults(searchQuery, abortController.signal);
      
      if (abortController.signal.aborted) {
        return;
      }

      if (searchResults && searchResults.all && searchResults.all.length > 0) {
        const groupedResults = groupResultsBySource(searchResults.all);
        setResults({ items: groupedResults, total: searchResults.all.length });
        setIsOpen(true);
      } else {
        setResults({ items: [], total: 0 });
        setIsOpen(true);
      }
      
      // Mark search as completed
      setHasSearchCompleted(true);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Search error:', error);
      setError(error);
      setResults({ items: [], total: 0 });
      setHasSearchCompleted(true);
    } finally {
      setIsLoading(false);
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [setResults, setIsLoading, setError, setIsOpen, setHasSearchCompleted, abortCurrentSearch]);

  // Direct search function - executes immediately without debounce
  const performDirectSearch = useCallback((searchQuery: string = query) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      abortCurrentSearch();
      resetResults();
      setIsOpen(false);
      return;
    }

    executeSearch(searchQuery);
  }, [query, executeSearch, abortCurrentSearch, resetResults, setIsOpen]);

  // Auto-search effect with debounce
  useEffect(() => {
    // Always reset results if query is empty or too short
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) {
      abortCurrentSearch();
      resetResults();
      setIsOpen(false);
      return;
    }

    // Execute debounced search
    executeSearch(debouncedQuery);
  }, [debouncedQuery, executeSearch, abortCurrentSearch, resetResults, setIsOpen]);

  return {
    abortCurrentSearch,
    performDirectSearch
  };
}
